import { useEffect, useRef, useState, type UIEvent } from "react";
import { Button } from "./button";

interface DeclaratoriaModalProps {
  open: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

const DECLARATORIA_PARAGRAPHS = [
  "La Benemérita Universidad Autónoma de Chiapas (UNACH) reafirma que la Convención Internacional UNACH (CIU) 2026 es un espacio académico plural, seguro y respetuoso, sustentado en los principios de dignidad humana, igualdad sustantiva, no discriminación, libertad de expresión responsable y convivencia pacífica.",
  "Al registrarme y participar en la CIU 2026, manifiesto mi compromiso de conducirme con apego a los valores y normativa institucional de la UNACH, manteniendo en todo momento una conducta ética, profesional y respetuosa de los derechos humanos de todas las personas asistentes, organizadoras, ponentes y colaboradoras.",
  "Reconozco que cualquier forma de violencia, acoso, hostigamiento, discriminación o conducta que vulnere la dignidad humana es contraria a los principios universitarios y no será tolerada en el marco de este evento.",
  "En caso de considerar que mis derechos humanos o universitarios han sido vulnerados durante el desarrollo de la Convención, podré comunicarlo de manera oportuna a la Defensoría de los Derechos Humanos y Universitarios de la UNACH, instancia competente para brindar orientación, acompañamiento y atención conforme a la normativa aplicable, al teléfono 961 61 78000 ext. 5046 y 5047, o al correo electrónico",
  "Asimismo, con fundamento en el artículo 87 de la Ley Federal de Derechos de Autor, otorgo mi autorización expresa para que mi imagen y/o voz puedan ser captadas mediante fotografías, grabaciones de audio o video durante el desarrollo de la CIU 2026, y utilizadas exclusivamente con fines institucionales, académicos, comunicacionales y de integración de la memoria del evento, en medios impresos, digitales o audiovisuales de la UNACH.",
  "He leído y acepto la Declaratoria de Compromiso Institucional para participar en la CIU 2026.",
];

function isScrolledToEnd(element: HTMLDivElement): boolean {
  return element.scrollTop + element.clientHeight >= element.scrollHeight - 8;
}

export function DeclaratoriaModal({ open, onAccept, onCancel }: DeclaratoriaModalProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  useEffect(() => {
    if (!open) return;

    setHasReachedEnd(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.setTimeout(() => {
      const content = contentRef.current;
      if (content && content.scrollHeight <= content.clientHeight + 8) {
        setHasReachedEnd(true);
      }
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (isScrolledToEnd(event.currentTarget)) {
      setHasReachedEnd(true);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 px-4 py-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="declaratoria-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-[940px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl md:max-h-[88vh]">
        <header className="border-b border-slate-200 px-5 py-5 text-center md:px-10 md:py-7">
          <h2 id="declaratoria-title" className="text-2xl font-bold leading-tight text-[#0f2744] md:text-4xl">
            Declaratoria de Compromiso Institucional
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500 md:text-lg">
            Convención Internacional UNACH (CIU) 2026
          </p>
        </header>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-6 text-[15px] leading-7 text-slate-700 md:px-11 md:py-8 md:text-lg md:leading-8"
        >
          <p className="mb-4 font-bold tracking-wide text-[#0f2744]">Declaro que:</p>
          <p className="mb-4">{DECLARATORIA_PARAGRAPHS[0]}</p>
          <p className="mb-4">{DECLARATORIA_PARAGRAPHS[1]}</p>
          <p className="mb-4">{DECLARATORIA_PARAGRAPHS[2]}</p>
          <p className="mb-4">
            {DECLARATORIA_PARAGRAPHS[3]}{" "}
            <a className="font-medium text-blue-600 underline" href="mailto:defensoria@unach.mx">
              defensoria@unach.mx
            </a>
          </p>
          <p className="mb-4">{DECLARATORIA_PARAGRAPHS[4]}</p>
          <p className="mb-2">{DECLARATORIA_PARAGRAPHS[5]}</p>
        </div>

        <footer className="border-t border-slate-200 bg-white px-5 py-4 md:px-10 md:py-5">
          {!hasReachedEnd ? (
            <div className="mb-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-500">
              <span aria-hidden="true">↓</span>
              <span>Continúa leyendo para habilitar</span>
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-end">
            <Button type="button" variant="outline" className="h-12 px-8 text-base" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="h-12 bg-[#0f2744] px-8 text-base font-semibold text-white hover:bg-[#17345b] disabled:bg-slate-300 disabled:text-white"
              disabled={!hasReachedEnd}
              onClick={onAccept}
            >
              Acepto el compromiso
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
